import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  CommentActionType,
  CommentAnchorCheckEvent,
  CommentAnchorKey,
} from './index.js';
import { openSectionCommentPanel, getSessionStorage } from './utils.js';

const anchorStyle = {
  height: 0,
  width: 0,
  opacity: 0,
  zIndex: -1000,
};

/**
 * HOC to wrapper component with comment panel
 * @param WrappedComponent
 * @param shouldScrollInToView
 * @param getAnchorIdFromProp
 * @param initialAnchorId
 * @param openCommentPanel
 * @returns React.FC
 * @constructor
 */
function CommentAnchor(
  WrappedComponent,
  {
    shouldScrollInToView,
    getAnchorIdFromProp = () => '',
    initialAnchorId,
    onDirectSuccess = openSectionCommentPanel,
  },
) {
  return function Wrapped(props) {
    const [anchorId, setAnchorId] = useState(
      initialAnchorId || getAnchorIdFromProp(props),
    );
    const consumeAnchorRecordRef = useRef(null);
    const consumeAnchorRecord = useCallback(() => {
      let consumed = false;
      let anchorRecord = getSessionStorage(CommentAnchorKey);
      if (anchorRecord) {
        try {
          anchorRecord = JSON.parse(anchorRecord);
          const actionType = anchorRecord?.actionType;
          if (
            actionType &&
            actionType !== CommentActionType.OpenSectionComment
          ) {
            return;
          }
          if (shouldScrollInToView(anchorRecord, anchorId)) {
            const anchorNode = document.querySelector(
              `[data-comment-anchor-id=\'${anchorId}\']`,
            );
            if (anchorNode) {
              anchorNode.scrollIntoViewIfNeeded();
              consumed = true;
              sessionStorage.removeItem(CommentAnchorKey);
              typeof onDirectSuccess === 'function' &&
                onDirectSuccess(anchorId, anchorRecord);
            }
          }
        } catch (e) {
          console.log(e);
        }
      }
      return consumed;
    }, [anchorId]);
    consumeAnchorRecordRef.current = consumeAnchorRecord;

    const setCommentAnchorId = useCallback((nextAnchorId) => {
      if (nextAnchorId) {
        setAnchorId(nextAnchorId);
        consumeAnchorRecord();
      }
    }, []);
    useLayoutEffect(() => {
      consumeAnchorRecord();
      const listener = () => {
        consumeAnchorRecordRef.current && consumeAnchorRecordRef.current();
      };
      window.addEventListener(CommentAnchorCheckEvent, listener);

      return () => {
        window.removeEventListener(CommentAnchorCheckEvent, listener);
      };
    }, []);
    return (
      <>
        <span style={anchorStyle} data-comment-anchor-id={anchorId} />
        <WrappedComponent
          setCommentAnchorId={setCommentAnchorId}
          commentAnchorId={anchorId}
          consumeAnchorRecord={consumeAnchorRecord}
          // pass props and ref
          {...props}
          // ref={ref}
        />
      </>
    );
  };
}

export default CommentAnchor;
